/**
 * Returns a sorted list of IANA timezone identifiers for use in selects.
 * Uses Intl.supportedValuesOf('timeZone') when available (modern browsers),
 * otherwise returns a fallback list of common timezones.
 */
const FALLBACK_TIMEZONES = [
  'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Cairo', 'Africa/Casablanca',
  'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'America/Argentina/Buenos_Aires', 'America/Bogota',
  'America/Caracas', 'America/Chicago', 'America/Denver', 'America/Fortaleza', 'America/Guayaquil', 'America/Halifax',
  'America/Lima', 'America/Los_Angeles', 'America/Manaus', 'America/Mexico_City', 'America/New_York', 'America/Noronha',
  'America/Panama', 'America/Recife', 'America/Santiago', 'America/Sao_Paulo', 'America/Toronto', 'Asia/Bangkok',
  'Asia/Dubai', 'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Kolkata', 'Asia/Seoul',
  'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo', 'Atlantic/Azores', 'Australia/Adelaide', 'Australia/Brisbane',
  'Australia/Perth', 'Australia/Sydney', 'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/London',
  'Europe/Moscow', 'Europe/Paris', 'Europe/Rome', 'Europe/Warsaw', 'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam',
  'Pacific/Honolulu', 'Etc/UTC', 'Etc/GMT', 'UTC',
];

export function getTimezones(): string[] {
  try {
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl && typeof (Intl as any).supportedValuesOf === 'function') {
      const list = (Intl as any).supportedValuesOf('timeZone') as string[];
      if (Array.isArray(list) && list.length > 0) {
        return [...list].sort((a, b) => a.localeCompare(b));
      }
    }
  } catch {
    // ignore
  }
  return [...FALLBACK_TIMEZONES].sort((a, b) => a.localeCompare(b));
}

/**
 * Location names / aliases that map to IANA timezone IDs (for search).
 * Florida and other US states/cities don't have their own IANA id; they use e.g. America/New_York.
 */
const LOCATION_ALIASES: Record<string, string[]> = {
  florida: ['America/New_York'],
  miami: ['America/New_York'],
  'new york': ['America/New_York'],
  eastern: ['America/New_York'],
  california: ['America/Los_Angeles'],
  'los angeles': ['America/Los_Angeles'],
  pacific: ['America/Los_Angeles'],
  texas: ['America/Chicago', 'America/Denver'],
  chicago: ['America/Chicago'],
  central: ['America/Chicago'],
  denver: ['America/Denver'],
  mountain: ['America/Denver'],
  phoenix: ['America/Phoenix'],
  arizona: ['America/Phoenix'],
  seattle: ['America/Los_Angeles'],
  'san francisco': ['America/Los_Angeles'],
  boston: ['America/New_York'],
  washington: ['America/New_York'],
  atlanta: ['America/New_York'],
  detroit: ['America/Detroit'],
  toronto: ['America/Toronto'],
  vancouver: ['America/Vancouver'],
  'são paulo': ['America/Sao_Paulo'],
  'sao paulo': ['America/Sao_Paulo'],
  brasil: ['America/Sao_Paulo', 'America/Manaus', 'America/Fortaleza'],
  brazil: ['America/Sao_Paulo', 'America/Manaus', 'America/Fortaleza'],
  london: ['Europe/London'],
  paris: ['Europe/Paris'],
  berlin: ['Europe/Berlin'],
  tokyo: ['Asia/Tokyo'],
  sydney: ['Australia/Sydney'],
};

/** Returns extra search terms for a timezone (e.g. "Florida" for America/New_York) so filter can match by location name. */
export function getTimezoneSearchTerms(tzId: string): string[] {
  const terms: string[] = [];
  for (const [alias, ids] of Object.entries(LOCATION_ALIASES)) {
    if (ids.includes(tzId)) terms.push(alias);
  }
  return terms;
}

/** Returns the GMT offset label for a timezone (e.g. "GMT-3", "GMT+5:30"). */
export function getTimezoneOffsetLabel(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'longOffset' });
    const parts = formatter.formatToParts(new Date());
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part?.value ?? '';
  } catch {
    return '';
  }
}

/** Returns offset in minutes from UTC (e.g. -180 for GMT-3, 330 for GMT+5:30). Used for sorting list from smallest to largest GMT. */
export function getTimezoneOffsetMinutes(tz: string): number {
  const label = getTimezoneOffsetLabel(tz);
  const match = label.match(/(?:GMT|UTC)([+-])(\d+)(?::(\d+))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;
  return sign * (hours * 60 + minutes);
}
